import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { apiService } from '@/services/api';
import OrderStatusBadge from '@/components/Orders/OrderStatusBadge/OrderStatusBadge';
import type { ChatMessage, Order } from '@/types/order';
import styles from './BranchChatPage.module.scss';

const POLL_MS = 10000;

export default function BranchChatPage() {
  const { branchId } = useParams<{ branchId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [threadId, setThreadId] = useState<string | null>(searchParams.get('thread'));
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [myOrders, setMyOrders] = useState<Order[]>([]);

  const cursorRef = useRef<string | null>(null);
  const seenRef = useRef<Set<string>>(new Set());
  const listRef = useRef<HTMLDivElement | null>(null);
  const mounted = useRef(true);

  const mergeMessages = useCallback((incoming: ChatMessage[]) => {
    if (incoming.length === 0) return;
    setMessages((prev) => {
      const fresh = incoming.filter((m) => !seenRef.current.has(m.id));
      fresh.forEach((m) => seenRef.current.add(m.id));
      if (fresh.length === 0) return prev;
      const merged = [...prev, ...fresh].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      const last = merged[merged.length - 1];
      if (last) cursorRef.current = last.createdAt;
      return merged;
    });
  }, []);

  // Resolve the thread for this branch if not provided.
  useEffect(() => {
    mounted.current = true;
    let active = true;
    (async () => {
      try {
        let tid = threadId;
        if (!tid && branchId) {
          const thread = await apiService.createChatThread(branchId);
          tid = thread.id;
          if (active) setThreadId(tid);
        }
        if (tid) {
          const { items } = await apiService.getChatMessages(tid);
          if (active) mergeMessages(items);
        }
      } catch (e: any) {
        if (active) setError(e?.message || 'Could not open chat.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; mounted.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  // Poll for new messages.
  useEffect(() => {
    if (!threadId) return;
    const id = setInterval(async () => {
      try {
        const { items } = await apiService.getChatMessages(threadId, cursorRef.current || undefined);
        if (mounted.current) mergeMessages(items);
      } catch { /* keep polling */ }
    }, POLL_MS);
    return () => clearInterval(id);
  }, [threadId, mergeMessages]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const handleSend = async () => {
    if (!threadId || !text.trim()) return;
    setSending(true);
    setError(null);
    try {
      const msg = await apiService.postChatMessage(threadId, { type: 'TEXT', body: text.trim() });
      mergeMessages([msg]);
      setText('');
    } catch (e: any) {
      setError(e?.message || 'Could not send message.');
    } finally {
      setSending(false);
    }
  };

  const openShare = async () => {
    setShareOpen((v) => !v);
    if (myOrders.length === 0) {
      try {
        const page = await apiService.getMyOrders({ pageSize: 20 });
        const forBranch = page.items.filter((o) => o.branchId === branchId);
        setMyOrders(forBranch.length > 0 ? forBranch : page.items);
      } catch { /* ignore */ }
    }
  };

  const shareOrder = async (order: Order) => {
    if (!threadId) return;
    setShareOpen(false);
    setSending(true);
    try {
      const msg = await apiService.postChatMessage(threadId, { type: 'ORDER_REF', orderId: order.id });
      mergeMessages([msg]);
    } catch (e: any) {
      setError(e?.message || 'Could not share order.');
    } finally {
      setSending(false);
    }
  };

  const renderMessage = (m: ChatMessage) => {
    const mine = m.senderRole === 'CUSTOMER';
    const sideClass = mine ? styles.mine : styles.theirs;

    if (m.type === 'ORDER_REF') {
      const o = m.order;
      return (
        <div key={m.id} className={`${styles.msgRow} ${sideClass}`}>
          <button
            type="button"
            className={styles.orderCard}
            onClick={() => o && navigate(`/customer/orders/${o.id}`)}
          >
            <div className={styles.orderCardTop}>
              <span className={styles.orderCardNo}>Order #{o?.orderNumber ?? '—'}</span>
              {o?.status && <OrderStatusBadge status={o.status} />}
            </div>
            <span className={styles.orderCardItems}>{o?.itemCount ?? 0} item{(o?.itemCount ?? 0) !== 1 ? 's' : ''}</span>
            <span className={styles.orderCardHint}>View order →</span>
          </button>
        </div>
      );
    }

    if (m.type === 'COMMENT') {
      return (
        <div key={m.id} className={`${styles.msgRow} ${sideClass}`}>
          <div className={styles.commentBubble}>
            <span className={styles.commentTag}>Comment</span>
            <span>{m.body}</span>
            <span className={styles.time}>{new Date(m.createdAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        </div>
      );
    }

    return (
      <div key={m.id} className={`${styles.msgRow} ${sideClass}`}>
        <div className={styles.bubble}>
          <span>{m.body}</span>
          <span className={styles.time}>{new Date(m.createdAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
      </div>
    );
  };

  return (
    <>
      <DashboardHeader />
      <div className={styles.container}>
        <div className={styles.topBar}>
          <button type="button" className={styles.backButton} onClick={() => navigate(-1)}>← Back</button>
          <h1 className={styles.title}>Branch chat</h1>
        </div>

        <div className={styles.messages} ref={listRef}>
          {loading && <p className={styles.muted}>Loading chat…</p>}
          {!loading && messages.length === 0 && !error && (
            <p className={styles.muted}>No messages yet. Say hello or share an order.</p>
          )}
          {messages.map(renderMessage)}
        </div>

        {error && <p className={styles.error}>{error}</p>}

        {shareOpen && (
          <div className={styles.sharePanel}>
            <span className={styles.shareTitle}>Share an order</span>
            {myOrders.length === 0 && <span className={styles.muted}>No orders to share.</span>}
            {myOrders.map((o) => (
              <button key={o.id} type="button" className={styles.shareItem} onClick={() => shareOrder(o)}>
                <span>Order #{o.orderNumber}</span>
                <span className={styles.muted}>{(o.items ?? []).length} items</span>
              </button>
            ))}
          </div>
        )}

        <div className={styles.composer}>
          <button type="button" className={styles.shareBtn} onClick={openShare} disabled={sending} aria-label="Share order">＋</button>
          <input
            className={styles.input}
            placeholder="Message the branch…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          />
          <button type="button" className={styles.sendBtn} onClick={handleSend} disabled={sending || !text.trim()}>Send</button>
        </div>
      </div>
    </>
  );
}
